import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  HStack,
  SimpleGrid,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  Tag,
  useToast,
  Select,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  useDisclosure,
} from "@chakra-ui/react";
import { RepeatIcon, AddIcon } from "@chakra-ui/icons";
import {
  fetchAccessLog,
  fetchControls,
  fetchOrders,
  assignOrderItem,
  unassignOrderItem,
  transitionOrderItem,
  updateOrderItem,
  createOrderItem,
  syncOrders,
  searchClinicPatients,
  searchUsers,
} from "../services/clinicComplianceService";
import AssigneePickerModal from "./clinic/AssigneePickerModal";
import { itemTypeLabel } from "./clinic/ordersFollowups";
import { normalizeStatus, STATUS_META, availableActions } from "../utils/workItemStatus";
import { onWorkItemsChanged, emitWorkItemsChanged } from "../utils/workItemsBus";
import { useUserInfo } from "../hooks/medstudent/useUserInfo";
import { supabaseSchema } from "./supabaseClient";

// Raw DB values a person can pick from the per-row status dropdown.
// "pending_review" and "rejected" are not selectable — they're reached only by
// the assignee's own "Mark done" / "Reject" (see WorkItemActions) — but they
// must still appear, disabled, so the dropdown can render them as the current
// value without silently snapping to something else.
const ORDER_STATUS_OPTIONS = ["unassigned", "pending", "pending_review", "completed", "rejected"];

// The review-flow action (backend/api/task_review.py) that moves `item` to the
// DB status `target` for this user, or null when that move isn't theirs to
// make. Built on availableActions so the dropdown and WorkItemActions agree:
//   Assigned row  → "Completed" = complete (the backend sends an assignee's
//                   completion to review)
//   Pending review → "Completed" = confirm, "Assigned" = send_back (assigner)
//                    or reopen (assignee)
//   Completed row → "Assigned" = reopen
export function statusTargetAction(item, target, userId) {
  if (!userId || !item?.assigned_to_user_id) return null;
  const status = normalizeStatus(item.status);
  const acts = availableActions(
    { status, assignedTo: item.assigned_to_user_id, assignedBy: item.assigned_by_user_id },
    userId,
  );
  if (target === "completed") {
    if (status === "assigned" && acts.includes("complete")) return "complete";
    if (status === "pending_review" && acts.includes("confirm")) return "confirm";
    return null;
  }
  if (target === "pending") {
    if (status === "pending_review") {
      if (acts.includes("send_back")) return "send_back";
      if (acts.includes("reopen")) return "reopen";
      return null;
    }
    if (status === "completed" && acts.includes("reopen")) return "reopen";
    return null;
  }
  return null;
}

const controlStatusColor = {
  passing: "green",
  attention: "yellow",
  failing: "red",
};

// order/task status colors and labels now come from utils/workItemStatus
// (STATUS_META) — the one vocabulary shared with tasks and every other
// surface that shows a work item's status.

const accessStatusColor = {
  ok: "green",
  failed: "red",
};

const frameworkColor = {
  SOC2: "blue",
  HIPAA: "teal",
};

function fmtTime(iso) {
  if (!iso) return "—";
  // Keep it simple + locale-stable: "Jun 12, 14:58"
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtScheduledDate(value) {
  if (!value) return "No scheduled date";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const UNASSIGNED_VALUE = "__unassigned__";
const CHOOSE_ASSIGNEE_VALUE = "__choose_assignee__";
const ITEM_TYPE_ORDER = { order: 0, followup: 1 };
const NEW_TASK_INITIAL = {
  patientQuery: "",
  selectedPatient: null,
  item_type: "order",
  description: "",
  assigneeQuery: "",
  selectedAssignee: null,
  status: "pending",
};

function assigneeLabel(user) {
  if (!user) return "Assigned user";
  return user.name || user.email || "Assigned user";
}

const TaskTableRow = ({
  item,
  knownAssignees,
  isBusy,
  onTaskChange,
  onAssigneeChange,
  onChooseAssignee,
  onStatusChange,
  currentUserId,
}) => {
  const [draft, setDraft] = useState(item.description || "");
  const taskTextareaRef = useRef(null);

  useEffect(() => {
    setDraft(item.description || "");
  }, [item.id, item.description]);

  useEffect(() => {
    const textarea = taskTextareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [draft]);

  const assigneeOptions = Array.from(
    knownAssignees.reduce((map, user) => {
      if (user?.id) map.set(user.id, user);
      return map;
    }, new Map()).values(),
  );
  if (item.assigned_to_user_id && !assigneeOptions.some((user) => user.id === item.assigned_to_user_id)) {
    assigneeOptions.push(item.assigned_to || { id: item.assigned_to_user_id });
  }

  const assignedValue = item.assigned_to_user_id || UNASSIGNED_VALUE;
  const currentStatus = item.status || "unassigned";

  const saveDraft = () => {
    const next = draft.trim();
    if (!next) {
      setDraft(item.description || "");
      return;
    }
    if (next !== (item.description || "")) onTaskChange(item, next);
  };

  return (
    <Tr _hover={{ bg: "gray.50" }}>
      <Td minW={{ base: "360px", lg: "520px" }} py={2}>
        <HStack align="flex-start" spacing={3}>
          <Badge colorScheme={item.item_type === "followup" ? "cyan" : "blue"} variant="subtle" mt={2} minW="74px" textAlign="center">
            {itemTypeLabel(item.item_type)}
          </Badge>
          <Textarea
            ref={taskTextareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={saveDraft}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                e.currentTarget.blur();
              }
            }}
            isDisabled={isBusy}
            rows={1}
            resize="none"
            overflow="hidden"
            minH="34px"
            size="sm"
            variant="filled"
            bg="white"
            borderColor="transparent"
            color="gray.800"
            _hover={{ borderColor: "gray.300" }}
            _focus={{ bg: "white", borderColor: "teal.400", boxShadow: "0 0 0 1px var(--chakra-colors-teal-400)" }}
          />
        </HStack>
      </Td>
      <Td minW="220px" py={2}>
        <Select
          size="sm"
          value={assignedValue}
          onChange={(e) => {
            const next = e.target.value;
            if (next === CHOOSE_ASSIGNEE_VALUE) {
              onChooseAssignee(item);
            } else if (next !== assignedValue) {
              onAssigneeChange(item, next);
            }
          }}
          isDisabled={isBusy}
          bg="white"
        >
          <option value={UNASSIGNED_VALUE}>Unassigned</option>
          {assigneeOptions.map((user) => (
            <option key={user.id} value={user.id}>
              {assigneeLabel(user)}
            </option>
          ))}
          <option value={CHOOSE_ASSIGNEE_VALUE}>Choose user...</option>
        </Select>
      </Td>
      <Td minW="160px" py={2}>
        <Select
          size="sm"
          value={currentStatus}
          onChange={(e) => onStatusChange(item, e.target.value)}
          // No user id yet → the backend can't tell assignee from assigner,
          // so no status change is safe until it loads.
          isDisabled={isBusy || !currentUserId}
          bg={`${(STATUS_META[normalizeStatus(currentStatus)] || STATUS_META.unassigned).colorScheme}.50`}
          fontWeight="semibold"
        >
          {ORDER_STATUS_OPTIONS.map((status) => (
            <option
              key={status}
              value={status}
              disabled={
                status !== currentStatus
                && status !== "unassigned"
                && !statusTargetAction(item, status, currentUserId)
              }
            >
              {(STATUS_META[normalizeStatus(status)] || STATUS_META.unassigned).label}
            </option>
          ))}
        </Select>
      </Td>
    </Tr>
  );
};

const ClinicComplianceDashboard = () => {
  const toast = useToast();
  const { userId } = useUserInfo();
  const pickerSchema = String(supabaseSchema || "public").split(",")[0].trim() || "public";
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState(null);
  const [controls, setControls] = useState(null);
  const [orders, setOrders] = useState(null);
  const [isSample, setIsSample] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [patientFilter, setPatientFilter] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [busyItemId, setBusyItemId] = useState(null);
  // assignee picker target: the item being assigned
  const assignDisclosure = useDisclosure();
  const [assignTarget, setAssignTarget] = useState(null);
  // add-task modal
  const addDisclosure = useDisclosure();
  const [newTask, setNewTask] = useState(NEW_TASK_INITIAL);
  const [patientOptions, setPatientOptions] = useState([]);
  const [searchingPatients, setSearchingPatients] = useState(false);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [searchingAssignees, setSearchingAssignees] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);

  const reloadOrders = useCallback(async () => {
    const ord = await fetchOrders();
    setOrders(ord);
  }, []);

  // Any other open surface (the Orders & followups modal, the audio panel)
  // mutating an order should refresh this dashboard too.
  useEffect(() => onWorkItemsChanged(reloadOrders), [reloadOrders]);

  const assignItemToUser = async (item, targetUserId) => {
    setBusyItemId(item.id);
    try {
      await assignOrderItem(item.id, targetUserId, { assignedBy: userId });
      emitWorkItemsChanged({ kind: "order" });
      await reloadOrders();
    } catch (err) {
      toast({ title: "Assign failed", description: err.message, status: "error", duration: 4000, isClosable: true });
    } finally {
      setBusyItemId(null);
    }
  };

  const handleAssign = async (user) => {
    if (!assignTarget) return;
    await assignItemToUser(assignTarget, user.id);
    setAssignTarget(null);
  };

  const handleUnassign = async (item) => {
    setBusyItemId(item.id);
    try {
      await unassignOrderItem(item.id);
      emitWorkItemsChanged({ kind: "order" });
      await reloadOrders();
    } catch (err) {
      toast({ title: "Unassign failed", description: err.message, status: "error", duration: 4000, isClosable: true });
    } finally {
      setBusyItemId(null);
    }
  };

  const handleAssigneeDropdown = async (item, nextUserId) => {
    if (nextUserId === UNASSIGNED_VALUE) {
      if (item.assigned_to_user_id) await handleUnassign(item);
      return;
    }
    await assignItemToUser(item, nextUserId);
  };

  // nextStatus is "pending" or "completed" (handleStatusDropdown routes
  // "unassigned" to handleUnassign). statusTargetAction maps it to the one
  // review-flow action this user may take; the dropdown already disables the
  // rest, so a null here is only a stale-row race.
  const handleSetStatus = async (item, nextStatus) => {
    const action = statusTargetAction(item, nextStatus, userId);
    if (!action) {
      toast({ title: "You can't make that change", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    setBusyItemId(item.id);
    try {
      await transitionOrderItem(item.id, action, { actorId: userId });
      emitWorkItemsChanged({ kind: "order" });
      await reloadOrders();
    } catch (err) {
      toast({ title: "Status update failed", description: err.message, status: "error", duration: 4000, isClosable: true });
    } finally {
      setBusyItemId(null);
    }
  };

  const handleStatusDropdown = async (item, nextStatus) => {
    if (nextStatus === item.status) return;
    if (nextStatus === "unassigned") {
      await handleUnassign(item);
      return;
    }
    if (!item.assigned_to_user_id) {
      toast({ title: "Assign this task first", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    await handleSetStatus(item, nextStatus);
  };

  const handleUpdateTaskText = async (item, description) => {
    const nextDescription = (description || "").trim();
    if (!nextDescription || nextDescription === (item.description || "")) return;
    setBusyItemId(item.id);
    try {
      await updateOrderItem(item.id, { description: nextDescription });
      emitWorkItemsChanged({ kind: "order" });
      await reloadOrders();
    } catch (err) {
      toast({ title: "Task update failed", description: err.message, status: "error", duration: 4000, isClosable: true });
    } finally {
      setBusyItemId(null);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncOrders();
      const syncParts = [`${res.inserted ?? 0} new task(s)`];
      if (res.date_repaired) syncParts.push(`${res.date_repaired} date(s) repaired`);
      if (res.generated) syncParts.push(`${res.generated} generated`);
      if (res.skipped_unstructured) syncParts.push(`${res.skipped_unstructured} raw transcript(s) skipped`);
      toast({ title: "Sync complete", description: `${syncParts.join(" · ")} from ${res.scanned ?? 0} recording(s).`, status: "success", duration: 5000, isClosable: true });
      emitWorkItemsChanged({ kind: "order" });
      await reloadOrders();
    } catch (err) {
      toast({ title: "Sync failed", description: err.message, status: "error", duration: 4000, isClosable: true });
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.selectedPatient) {
      toast({ title: "Select a patient from the dropdown", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    if (!newTask.description.trim()) {
      toast({ title: "Description required", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    setCreatingTask(true);
    try {
      const assignee = newTask.selectedAssignee;
      await createOrderItem({
        patient_name: newTask.selectedPatient.patient_name,
        ehr_id: newTask.selectedPatient.ehr_id,
        item_type: newTask.item_type,
        description: newTask.description.trim(),
        assigned_to_user_id: assignee?.id,
        status: assignee ? newTask.status : "unassigned",
      });
      addDisclosure.onClose();
      setNewTask(NEW_TASK_INITIAL);
      setPatientOptions([]);
      setAssigneeOptions([]);
      emitWorkItemsChanged({ kind: "order" });
      await reloadOrders();
    } catch (err) {
      toast({ title: "Could not create task", description: err.message, status: "error", duration: 4000, isClosable: true });
    } finally {
      setCreatingTask(false);
    }
  };

  const handleCloseAddTask = () => {
    addDisclosure.onClose();
    setNewTask(NEW_TASK_INITIAL);
    setPatientOptions([]);
    setAssigneeOptions([]);
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [acc, ctrl, ord] = await Promise.all([
        fetchAccessLog(),
        fetchControls(),
        fetchOrders(),
      ]);
      setAccess(acc);
      setControls(ctrl);
      setOrders(ord);
      setIsSample(Boolean(acc._sample || ctrl._sample || ord._sample));
    } catch (err) {
      toast({
        title: "Could not load clinic compliance data",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!addDisclosure.isOpen || newTask.selectedPatient || newTask.patientQuery.trim().length < 1) {
      setPatientOptions([]);
      setSearchingPatients(false);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setSearchingPatients(true);
      try {
        const res = await searchClinicPatients(newTask.patientQuery, { signal: controller.signal });
        setPatientOptions(res.patients || []);
      } catch (err) {
        if (err.name !== "AbortError") {
          setPatientOptions([]);
          toast({ title: "Could not search patients", description: err.message, status: "warning", duration: 3000, isClosable: true });
        }
      } finally {
        setSearchingPatients(false);
      }
    }, 250);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [addDisclosure.isOpen, newTask.patientQuery, newTask.selectedPatient, toast]);

  useEffect(() => {
    if (!addDisclosure.isOpen || newTask.selectedAssignee || newTask.assigneeQuery.trim().length < 1) {
      setAssigneeOptions([]);
      setSearchingAssignees(false);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setSearchingAssignees(true);
      try {
        const res = await searchUsers(newTask.assigneeQuery, { signal: controller.signal });
        setAssigneeOptions(res.users || []);
      } catch (err) {
        if (err.name !== "AbortError") {
          setAssigneeOptions([]);
          toast({ title: "Could not search users", description: err.message, status: "warning", duration: 3000, isClosable: true });
        }
      } finally {
        setSearchingAssignees(false);
      }
    }, 250);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [addDisclosure.isOpen, newTask.assigneeQuery, newTask.selectedAssignee, toast]);

  if (loading && !access) {
    return (
      <Box py={20} textAlign="center">
        <Spinner size="xl" color="teal.500" thickness="4px" />
        <Text mt={4} fontSize="lg" fontWeight="medium" color="gray.600">
          Loading internal compliance posture…
        </Text>
      </Box>
    );
  }

  const accessSummary = access?.summary || {};
  const events = access?.events || [];
  const controlsSummary = controls?.summary || {};
  const controlRows = controls?.controls || [];
  const ordersSummary = orders?.summary || {};
  const orderPatients = orders?.patients || [];
  const filteredPatients = orderPatients
    .map((p) => ({
      ...p,
      items: p.items.filter(
        (it) =>
          (statusFilter === "all" || it.status === statusFilter) &&
          (!patientFilter ||
            (p.patient_name || "").toLowerCase().includes(patientFilter.toLowerCase()) ||
            (p.ehr_id || "").toLowerCase().includes(patientFilter.toLowerCase())),
      ),
    }))
    .filter((p) => p.items.length > 0);
  const dateGroups = Array.from(
    filteredPatients.reduce((dateMap, patient) => {
      const itemsByDate = patient.items.reduce((itemMap, item) => {
        const key = item.scheduled_date || "";
        if (!itemMap.has(key)) itemMap.set(key, []);
        itemMap.get(key).push(item);
        return itemMap;
      }, new Map());

      itemsByDate.forEach((items, scheduledDate) => {
        if (!dateMap.has(scheduledDate)) {
          dateMap.set(scheduledDate, { scheduled_date: scheduledDate, patients: [], item_count: 0 });
        }
        const group = dateMap.get(scheduledDate);
        group.patients.push({ ...patient, items });
        group.item_count += items.length;
      });

      return dateMap;
    }, new Map()).values(),
  ).sort((a, b) => {
    if (a.scheduled_date && b.scheduled_date) return b.scheduled_date.localeCompare(a.scheduled_date);
    if (a.scheduled_date) return -1;
    if (b.scheduled_date) return 1;
    return 0;
  });
  const knownAssignees = Array.from(
    orderPatients.reduce((map, patient) => {
      (patient.items || []).forEach((item) => {
        if (item.assigned_to?.id) map.set(item.assigned_to.id, item.assigned_to);
      });
      return map;
    }, new Map()).values(),
  ).sort((a, b) => assigneeLabel(a).localeCompare(assigneeLabel(b)));

  return (
    <VStack align="stretch" spacing={8} p={1}>
      {/* Gradient header */}
      <Box
        bgGradient="linear(135deg, #134E4A 0%, #0F2A28 100%)"
        borderRadius="2xl"
        p={6}
        boxShadow="xl"
        color="white"
      >
        <HStack justify="space-between" flexWrap="wrap" gap={4}>
          <Box>
            <HStack mb={1} spacing={3}>
              <Text fontSize="2xl" fontWeight="extrabold" letterSpacing="-0.02em">
                Clinic & Internal Compliance
              </Text>
              <Badge colorScheme="teal" variant="solid" borderRadius="full" px={3} py={0.5} fontSize="xs">
                SOC 2 · HIPAA
              </Badge>
              {isSample && (
                <Badge colorScheme="orange" variant="solid" borderRadius="full" px={3} py={0.5} fontSize="xs">
                  Sample data
                </Badge>
              )}
            </HStack>
            <Text fontSize="sm" color="gray.300">
              Access activity logging, security control posture, and order
              lifecycle tracking for SOC 2 and HIPAA audit readiness.
            </Text>
          </Box>
          <Button
            leftIcon={<RepeatIcon />}
            colorScheme="teal"
            size="md"
            onClick={refresh}
            isLoading={loading}
            loadingText="Refreshing"
          >
            Refresh
          </Button>
        </HStack>
      </Box>

      {/* KPI row */}
      <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={6}>
        <Stat p={5} bg="white" borderRadius="2xl" boxShadow="lg" borderLeft="5px solid" borderLeftColor="teal.500" _hover={{ transform: "translateY(-4px)", transition: "all 0.2s" }}>
          <StatLabel fontWeight="bold" color="gray.500" fontSize="sm">Access Events (24h)</StatLabel>
          <StatNumber fontSize="3xl" fontWeight="black" mt={1}>{accessSummary.events_24h ?? 0}</StatNumber>
          <StatHelpText mb={0}>{accessSummary.unique_users ?? 0} unique users</StatHelpText>
        </Stat>
        <Stat p={5} bg="white" borderRadius="2xl" boxShadow="lg" borderLeft="5px solid" borderLeftColor="red.400" _hover={{ transform: "translateY(-4px)", transition: "all 0.2s" }}>
          <StatLabel fontWeight="bold" color="gray.500" fontSize="sm">Failed Auth Attempts</StatLabel>
          <StatNumber fontSize="3xl" fontWeight="black" mt={1}>{accessSummary.failed_auth ?? 0}</StatNumber>
          <StatHelpText mb={0}>{accessSummary.exports ?? 0} data exports logged</StatHelpText>
        </Stat>
        <Stat p={5} bg="white" borderRadius="2xl" boxShadow="lg" borderLeft="5px solid" borderLeftColor="green.500" _hover={{ transform: "translateY(-4px)", transition: "all 0.2s" }}>
          <StatLabel fontWeight="bold" color="gray.500" fontSize="sm">Controls Passing</StatLabel>
          <StatNumber fontSize="3xl" fontWeight="black" mt={1}>
            {controlsSummary.passing ?? 0}/{controlsSummary.total ?? 0}
          </StatNumber>
          <HStack spacing={2} mt={2}>
            <Badge colorScheme="yellow">{controlsSummary.attention ?? 0} Attention</Badge>
            <Badge colorScheme="red">{controlsSummary.failing ?? 0} Failing</Badge>
          </HStack>
        </Stat>
        <Stat p={5} bg="white" borderRadius="2xl" boxShadow="lg" borderLeft="5px solid" borderLeftColor="blue.500" _hover={{ transform: "translateY(-4px)", transition: "all 0.2s" }}>
          <StatLabel fontWeight="bold" color="gray.500" fontSize="sm">Orders & Followups Completed</StatLabel>
          <StatNumber fontSize="3xl" fontWeight="black" mt={1}>
            {ordersSummary.completed ?? 0}/{ordersSummary.total ?? 0}
          </StatNumber>
          <StatHelpText mb={0}>
            {ordersSummary.pending ?? 0} assigned · {ordersSummary.pending_review ?? 0} pending review ·{" "}
            {ordersSummary.unassigned ?? 0} unassigned
          </StatHelpText>
        </Stat>
      </SimpleGrid>

      {/* Controls tracking */}
      <Box bg="white" borderRadius="2xl" boxShadow="xl" p={6}>
        <Text fontSize="lg" fontWeight="bold" mb={1} color="gray.800">
          Security Controls Tracking
        </Text>
        <Text fontSize="xs" color="gray.500" mb={4}>
          SOC 2 trust-services criteria and HIPAA Security Rule safeguards — owner, status, and last review date.
        </Text>
        <Box overflowX="auto">
          <Table variant="simple" size="md">
            <Thead bg="gray.50">
              <Tr>
                <Th>Control</Th>
                <Th>Framework</Th>
                <Th>Name</Th>
                <Th>Owner</Th>
                <Th>Status</Th>
                <Th>Last Reviewed</Th>
                <Th>Evidence</Th>
              </Tr>
            </Thead>
            <Tbody>
              {controlRows.map((c) => (
                <Tr key={c.id} _hover={{ bg: "gray.50" }}>
                  <Td fontWeight="bold" color="gray.800">{c.id}</Td>
                  <Td>
                    <Badge colorScheme={frameworkColor[c.framework] || "gray"} variant="subtle">
                      {c.framework}
                    </Badge>
                  </Td>
                  <Td fontSize="sm" color="gray.700">{c.name}</Td>
                  <Td fontSize="sm">{c.owner || "—"}</Td>
                  <Td>
                    <Badge colorScheme={controlStatusColor[c.status] || "gray"} variant="solid" borderRadius="md" px={2} textTransform="capitalize">
                      {c.status}
                    </Badge>
                  </Td>
                  <Td fontSize="sm" color="gray.600">{c.last_reviewed || "—"}</Td>
                  <Td fontSize="xs" color="gray.500">{c.evidence || "—"}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>

      {/* Orders & Followups */}
      <Box bg="white" borderRadius="2xl" boxShadow="xl" p={6}>
        <HStack justify="space-between" flexWrap="wrap" gap={3} mb={1}>
          <Box>
            <Text fontSize="lg" fontWeight="bold" color="gray.800">
              Orders & Followups
            </Text>
            <Text fontSize="xs" color="gray.500">
              Action items extracted from visit recordings. Assign to a team member and track
              unassigned → pending → completed.
            </Text>
          </Box>
          <HStack spacing={2}>
            <Button size="sm" leftIcon={<AddIcon />} variant="outline" colorScheme="purple" onClick={addDisclosure.onOpen}>
              Add task
            </Button>
            <Button size="sm" leftIcon={<RepeatIcon />} colorScheme="teal" onClick={handleSync} isLoading={syncing} loadingText="Syncing">
              Sync
            </Button>
          </HStack>
        </HStack>

        {/* summary cards */}
        <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4} my={5}>
          {[
            { label: "Unassigned", value: ordersSummary.unassigned, color: "gray" },
            { label: "Assigned", value: ordersSummary.pending, color: "purple" },
            { label: "Pending review", value: ordersSummary.pending_review, color: "orange" },
            { label: "Completed", value: ordersSummary.completed, color: "green" },
            { label: "Total", value: ordersSummary.total, color: "blue" },
          ].map((s) => (
            <Box key={s.label} bg={`${s.color}.50`} borderRadius="xl" p={4} borderLeft="4px solid" borderLeftColor={`${s.color}.400`}>
              <Text fontSize="xs" fontWeight="bold" color={`${s.color}.700`} textTransform="uppercase" letterSpacing="0.05em">
                {s.label}
              </Text>
              <Text fontSize="2xl" fontWeight="black" color="gray.800">{s.value ?? 0}</Text>
            </Box>
          ))}
        </SimpleGrid>

        {/* toolbar */}
        <HStack spacing={3} mb={4} flexWrap="wrap">
          <Select size="sm" maxW="200px" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            {ORDER_STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{(STATUS_META[normalizeStatus(s)] || STATUS_META.unassigned).label}</option>
            ))}
          </Select>
          <Input size="sm" maxW="260px" placeholder="Filter by patient or EHR…" value={patientFilter} onChange={(e) => setPatientFilter(e.target.value)} />
        </HStack>

        {/* scheduled-date groups */}
        {filteredPatients.length === 0 ? (
          <Box py={10} textAlign="center" color="gray.400">
            <Text fontSize="sm">No orders or followups yet. Generate A&amp;P for a recording, or click <b>Sync</b>.</Text>
          </Box>
        ) : (
          <VStack align="stretch" spacing={6}>
            {dateGroups.map((dateGroup) => (
              <Box key={dateGroup.scheduled_date || "no-date"}>
                <HStack
                  justify="space-between"
                  borderBottom="1px solid"
                  borderColor="gray.200"
                  pb={2}
                  mb={3}
                  flexWrap="wrap"
                  gap={2}
                >
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.800">
                      {fmtScheduledDate(dateGroup.scheduled_date)}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      Scheduled visit date
                    </Text>
                  </Box>
                  <Badge colorScheme="teal" variant="subtle" borderRadius="md" px={2}>
                    {dateGroup.item_count} task{dateGroup.item_count === 1 ? "" : "s"}
                  </Badge>
                </HStack>

                <VStack align="stretch" spacing={4}>
                  {dateGroup.patients.map((p) => {
                    const taskRows = [...p.items].sort(
                      (a, b) => (ITEM_TYPE_ORDER[a.item_type] ?? 9) - (ITEM_TYPE_ORDER[b.item_type] ?? 9),
                    );
                    return (
                      <Box key={`${dateGroup.scheduled_date || "no-date"}-${p.ehr_id}-${p.patient_name}`} border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                        <HStack px={4} py={3} spacing={2} bg="gray.50" borderBottom="1px solid" borderColor="gray.200" flexWrap="wrap" gap={2}>
                          <Text fontSize="sm" fontWeight="bold" color="gray.800">{p.patient_name || "Unknown"}</Text>
                          {p.ehr_id && <Badge colorScheme="gray" variant="subtle">EHR {p.ehr_id}</Badge>}
                          <Badge colorScheme="teal" variant="subtle" ml="auto">
                            {p.items.length} task{p.items.length === 1 ? "" : "s"}
                          </Badge>
                        </HStack>
                        <Box overflowX="auto">
                          <Table size="sm" variant="simple">
                            <Thead bg="white">
                              <Tr>
                                <Th minW={{ base: "360px", lg: "520px" }}>Task</Th>
                                <Th minW="220px">Assigned to</Th>
                                <Th minW="160px">Status</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {taskRows.map((item) => (
                                <TaskTableRow
                                  key={item.id}
                                  item={item}
                                  knownAssignees={knownAssignees}
                                  isBusy={busyItemId === item.id}
                                  onTaskChange={handleUpdateTaskText}
                                  onAssigneeChange={handleAssigneeDropdown}
                                  onChooseAssignee={(target) => {
                                    setAssignTarget(target);
                                    assignDisclosure.onOpen();
                                  }}
                                  onStatusChange={handleStatusDropdown}
                                  currentUserId={userId}
                                />
                              ))}
                            </Tbody>
                          </Table>
                        </Box>
                      </Box>
                    );
                  })}
                </VStack>
              </Box>
            ))}
          </VStack>
        )}
      </Box>

      {/* Access activity log */}
      <Box bg="white" borderRadius="2xl" boxShadow="xl" p={6}>
        <Text fontSize="lg" fontWeight="bold" mb={1} color="gray.800">
          Access Activity Log
        </Text>
        <Text fontSize="xs" color="gray.500" mb={4}>
          Who accessed which page or record — the audit trail required by SOC 2 CC7 and HIPAA §164.312(b).
        </Text>
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Time</Th>
                <Th>User</Th>
                <Th>Role</Th>
                <Th>Action</Th>
                <Th>Resource / Page</Th>
                <Th>IP</Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {events.map((e) => (
                <Tr key={e.id} _hover={{ bg: "gray.50" }}>
                  <Td fontSize="sm" color="gray.600" whiteSpace="nowrap">{fmtTime(e.at)}</Td>
                  <Td fontSize="sm" fontWeight="medium">{e.user}</Td>
                  <Td>
                    <Tag size="sm" variant="subtle" colorScheme="gray" textTransform="capitalize">
                      {e.role}
                    </Tag>
                  </Td>
                  <Td fontSize="sm" textTransform="capitalize">{e.action}</Td>
                  <Td fontSize="sm" color="gray.700">{e.resource}</Td>
                  <Td fontSize="xs" color="gray.500" fontFamily="mono">{e.ip}</Td>
                  <Td>
                    <Badge colorScheme={accessStatusColor[e.status] || "gray"} variant="subtle" textTransform="uppercase">
                      {e.status}
                    </Badge>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>

      <AssigneePickerModal
        isOpen={assignDisclosure.isOpen}
        onClose={() => { assignDisclosure.onClose(); setAssignTarget(null); }}
        onSelect={handleAssign}
        title={assignTarget ? `Assign: ${assignTarget.description}` : "Assign to user"}
        schema={pickerSchema}
      />

      <Modal isOpen={addDisclosure.isOpen} onClose={handleCloseAddTask}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add task</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">Patient name</FormLabel>
                <Input
                  value={newTask.patientQuery}
                  onChange={(e) => setNewTask({
                    ...newTask,
                    patientQuery: e.target.value,
                    selectedPatient: null,
                  })}
                  placeholder="Search patient name or EHR..."
                />
                {newTask.patientQuery && !newTask.selectedPatient && (
                  <Box mt={2} border="1px solid" borderColor="gray.200" borderRadius="md" maxH="180px" overflowY="auto">
                    {searchingPatients ? (
                      <Text fontSize="sm" color="gray.500" textAlign="center" py={3}>Searching...</Text>
                    ) : patientOptions.length === 0 ? (
                      <Text fontSize="sm" color="gray.500" textAlign="center" py={3}>No matching patients</Text>
                    ) : (
                      <VStack align="stretch" spacing={0}>
                        {patientOptions.map((patient) => (
                          <Box
                            key={`${patient.ehr_id || "no-ehr"}-${patient.patient_name}`}
                            px={3}
                            py={2}
                            cursor="pointer"
                            borderBottom="1px solid"
                            borderColor="gray.100"
                            _hover={{ bg: "gray.50" }}
                            onClick={() => {
                              setNewTask({
                                ...newTask,
                                patientQuery: patient.patient_name || "",
                                selectedPatient: patient,
                              });
                              setPatientOptions([]);
                            }}
                          >
                            <Text fontSize="sm" fontWeight="bold">{patient.patient_name || "Unknown"}</Text>
                            <Text fontSize="xs" color="gray.500">EHR {patient.ehr_id || "none"}</Text>
                          </Box>
                        ))}
                      </VStack>
                    )}
                  </Box>
                )}
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">EHR / MRN</FormLabel>
                <Input value={newTask.selectedPatient?.ehr_id || ""} isReadOnly placeholder="Select a patient first" bg="gray.50" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Type</FormLabel>
                <Select value={newTask.item_type} onChange={(e) => setNewTask({ ...newTask, item_type: e.target.value })}>
                  <option value="order">Order</option>
                  <option value="followup">Followup</option>
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Description</FormLabel>
                <Textarea value={newTask.description} onChange={(e) => setNewTask({ ...newTask, description: e.target.value })} placeholder="e.g. Order A1c lab" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Assign to</FormLabel>
                <Input
                  value={newTask.assigneeQuery}
                  onChange={(e) => setNewTask({
                    ...newTask,
                    assigneeQuery: e.target.value,
                    selectedAssignee: null,
                    status: "pending",
                  })}
                  placeholder="Optional: search user by name or email..."
                />
                {newTask.assigneeQuery && !newTask.selectedAssignee && (
                  <Box mt={2} border="1px solid" borderColor="gray.200" borderRadius="md" maxH="180px" overflowY="auto">
                    {searchingAssignees ? (
                      <Text fontSize="sm" color="gray.500" textAlign="center" py={3}>Searching...</Text>
                    ) : assigneeOptions.length === 0 ? (
                      <Text fontSize="sm" color="gray.500" textAlign="center" py={3}>No matching users</Text>
                    ) : (
                      <VStack align="stretch" spacing={0}>
                        {assigneeOptions.map((user) => (
                          <Box
                            key={user.id}
                            px={3}
                            py={2}
                            cursor="pointer"
                            borderBottom="1px solid"
                            borderColor="gray.100"
                            _hover={{ bg: "gray.50" }}
                            onClick={() => {
                              setNewTask({
                                ...newTask,
                                assigneeQuery: assigneeLabel(user),
                                selectedAssignee: user,
                                status: "pending",
                              });
                              setAssigneeOptions([]);
                            }}
                          >
                            <Text fontSize="sm" fontWeight="bold">{assigneeLabel(user)}</Text>
                            {user.name && <Text fontSize="xs" color="gray.500">{user.email}</Text>}
                          </Box>
                        ))}
                      </VStack>
                    )}
                  </Box>
                )}
              </FormControl>
              {newTask.selectedAssignee && (
                <FormControl>
                  <FormLabel fontSize="sm">Initial status</FormLabel>
                  <Select value={newTask.status} onChange={(e) => setNewTask({ ...newTask, status: e.target.value })}>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                  </Select>
                </FormControl>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseAddTask}>Cancel</Button>
            <Button
              colorScheme="purple"
              onClick={handleCreateTask}
              isLoading={creatingTask}
              isDisabled={!newTask.selectedPatient || !newTask.description.trim()}
            >
              Add task
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default ClinicComplianceDashboard;
